
import React from 'react';
import { Separator } from "@/components/ui/separator"
import {
  LayoutDashboard,
  Settings,
  Book,
  Users,
  GraduationCap,
  FileEdit,
  Shield,
  CalendarClock,
  User,
  Brain,
  Code,
  Network,
  Bot,
  MessageSquare,
  ListChecks,
  CheckSquare
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from "@tanstack/react-query";
import RoleDebugger from '../debug/RoleDebugger';

// Define a type for the navigation items to ensure type safety
type NavItem = {
  label: string;
  href: string;
  icon: React.ReactNode;
  roles?: string[];
};

const Sidebar = () => {
  const location = useLocation();
  const { 
    user, 
    profile, 
    viewMode,
    signOut, 
    resetViewMode,
    isAdmin, 
    isMentor,
    canAccessAdminFeatures,
    canAccessMentorFeatures,
    canAccessAgenticFeatures 
  } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isUserAdmin = isAdmin();
  const isUserMentor = isMentor();
  const showAdminNav = canAccessAdminFeatures();
  const showMentorNav = canAccessMentorFeatures();
  const showAgenticNav = canAccessAgenticFeatures();

  const handleSignOut = async () => {
    try {
      await signOut();
      queryClient.clear()
      navigate('/auth/signin');
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
    } catch (error) {
      console.error('Sign out failed:', error);
      toast({
        title: "Error signing out",
        description: "There was an error signing you out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const standardNavItems: NavItem[] = [
    {
      label: 'Dashboard',
      href: '/dashboard',
      icon: <LayoutDashboard className="h-5 w-5" />,
    },
    {
      label: 'Modules',
      href: '/modules',
      icon: <Book className="h-5 w-5" />,
    },
    {
      label: 'Projects',
      href: '/projects',
      icon: <Code className="h-5 w-5" />,
    },
    {
      label: 'Community',
      href: '/community',
      icon: <Users className="h-5 w-5" />,
    },
    {
      label: 'Second Me',
      href: '/second-me',
      icon: <Brain className="h-5 w-5" />,
    },
    {
      label: 'Personality',
      href: '/personality',
      icon: <User className="h-5 w-5" />,
    },
    {
      label: 'Office Hours',
      href: '/office-hours',
      icon: <CalendarClock className="h-5 w-5" />,
    },
    {
      label: 'Portfolio',
      href: '/portfolio',
      icon: <FileEdit className="h-5 w-5" />,
    },
  ];

  const adminNavItems: NavItem[] = [
    {
      label: 'Admin Dashboard',
      href: '/admin',
      icon: <Shield className="h-5 w-5" />,
    },
    {
      label: 'User Management',
      href: '/admin/users',
      icon: <Users className="h-5 w-5" />,
    },
    {
      label: 'Course Builder',
      href: '/admin/course-builder',
      icon: <Book className="h-5 w-5" />,
    },
    {
      label: 'Mentor Management',
      href: '/admin/mentors',
      icon: <GraduationCap className="h-5 w-5" />,
    },
    {
      label: 'System Settings',
      href: '/admin/settings',
      icon: <Settings className="h-5 w-5" />,
    },
  ];

  const mentorNavItems: NavItem[] = [
    {
      label: 'Mentor Dashboard',
      href: '/mentor',
      icon: <GraduationCap className="h-5 w-5" />,
    },
    {
      label: 'Course Builder',
      href: '/mentor/course-builder',
      icon: <Book className="h-5 w-5" />,
    },
    {
      label: 'Office Hours',
      href: '/mentor/office-hours',
      icon: <CalendarClock className="h-5 w-5" />,
    },
    {
      label: 'Project Reviews',
      href: '/mentor/projects',
      icon: <Code className="h-5 w-5" />,
    },
    {
      label: 'Students',
      href: '/mentor/students',
      icon: <Users className="h-5 w-5" />,
    },
  ];

  const agenticNavItems: NavItem[] = [
    {
      label: 'Agentic Dashboard',
      href: '/agentic',
      icon: <Brain className="h-5 w-5" />,
    },
    {
      label: 'Agent Workflows',
      href: '/agentic/workflows',
      icon: <Network className="h-5 w-5" />,
    },
    {
      label: 'Agent Capabilities',
      href: '/agentic/capabilities',
      icon: <Bot className="h-5 w-5" />,
    },
    {
      label: 'Prompt Library',
      href: '/agentic/prompts',
      icon: <MessageSquare className="h-5 w-5" />,
    },
    {
      label: 'Execution History',
      href: '/agentic/executions',
      icon: <ListChecks className="h-5 w-5" />,
    },
    {
      label: 'Dev To-Do List',
      href: '/agentic/dev-todo',
      icon: <CheckSquare className="h-5 w-5" />,
      roles: ["admin", "mentor"]
    },
  ];

  return (
    <div className="h-full w-full flex flex-col gap-4 p-4 bg-background border-r">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="justify-start px-0 font-normal relative">
            <Avatar className="mr-2 h-8 w-8">
              <AvatarImage src={profile?.profile_photo_url || ""} />
              <AvatarFallback className="bg-brand-indigo text-white">{profile?.first_name?.[0]}{profile?.last_name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col text-left">
              <span className="dark:text-white">{profile?.first_name} {profile?.last_name}</span>
              <span className="text-muted-foreground">{user?.email}</span>
            </div>
            
            {viewMode !== 'default' && (
              <div className="absolute top-0 right-0 h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-sky-500"></span>
              </div>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-80" align="start">
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuItem>
            <NavLink to="/profile" className="w-full">
              Profile
            </NavLink>
          </DropdownMenuItem>
          
          {viewMode !== 'default' && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={resetViewMode}>
                <Shield className="mr-2 h-4 w-4" />
                Exit {viewMode.charAt(0).toUpperCase() + viewMode.slice(1)} View Mode
              </DropdownMenuItem>
            </>
          )}
          
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut}>
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Separator />

      <nav className="flex flex-col space-y-1">
        {standardNavItems.map((item) => (
          <NavLink
            key={item.label}
            to={item.href}
            className={({ isActive }) =>
              `flex items-center space-x-2 rounded-md p-2 text-sm font-medium hover:bg-secondary ${
                isActive ? 'bg-secondary dark:bg-brand-indigo dark:bg-opacity-20 dark:text-brand-indigo-light' : 'text-muted-foreground'
              }`
            }
          >
            {item.icon}
            <span>{item.label}</span>
          </NavLink>
        ))}
        
        {showMentorNav && (
          <>
            <Separator className="my-2" />
            <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Mentor</div>
            
            {mentorNavItems.map((item) => (
              <NavLink
                key={item.label}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center space-x-2 rounded-md p-2 text-sm font-medium hover:bg-secondary ${
                    isActive ? 'bg-secondary dark:bg-brand-indigo dark:bg-opacity-20 dark:text-brand-indigo-light' : 'text-muted-foreground'
                  }`
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}
        
        {showAdminNav && (
          <>
            <Separator className="my-2" />
            <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Admin</div>
            
            {adminNavItems.map((item) => (
              <NavLink
                key={item.label}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center space-x-2 rounded-md p-2 text-sm font-medium hover:bg-secondary ${
                    isActive ? 'bg-secondary dark:bg-brand-indigo dark:bg-opacity-20 dark:text-brand-indigo-light' : 'text-muted-foreground'
                  }`
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}

        {showAgenticNav && (
          <>
            <Separator className="my-2" />
            <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Agentic Platform</div>
            
            {agenticNavItems.map((item) => (
              <NavLink
                key={item.label}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center space-x-2 rounded-md p-2 text-sm font-medium hover:bg-secondary ${
                    isActive ? 'bg-secondary dark:bg-brand-indigo dark:bg-opacity-20 dark:text-brand-indigo-light' : 'text-muted-foreground'
                  }`
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>
      
      <div className="mt-auto">
        <RoleDebugger />
      </div>
    </div>
  );
};

export default Sidebar;
